import {
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import AvatarBoCan from '../../AvatarBoCan'
import { AVATAR_BO_PERMISSIONS } from '../../shared/permissions'
import {
  canApprove,
  canDecline,
  canPublish,
  type VersionRow,
} from '../../shared/publish-view'

export interface VersionsTableProps {
  rows: VersionRow[]
  loading?: boolean
  busy?: boolean
  onApprove: (n: number) => void
  onDecline: (n: number) => void
  onPublish: (n: number, isRollback: boolean) => void
  /** Opens the what-changed dialog for one version. */
  onInspect?: (n: number) => void
}

export default function VersionsTable({
  rows,
  loading = false,
  busy = false,
  onApprove,
  onDecline,
  onPublish,
  onInspect,
}: VersionsTableProps) {
  const { formatMessage } = useIntl()

  if (loading) {
    return (
      <Stack spacing={1} data-testid="avatar-versions-skeleton">
        {Array.from({ length: 3 }, (_v, i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Stack>
    )
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {formatMessage({ id: 'avatarStudio.publish.noVersions' })}
      </Typography>
    )
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{formatMessage({ id: 'avatarStudio.publish.version' })}</TableCell>
          <TableCell>{formatMessage({ id: 'avatarStudio.publish.stagedBy' })}</TableCell>
          <TableCell>{formatMessage({ id: 'avatarStudio.publish.reviewedBy' })}</TableCell>
          <TableCell align="right">
            {formatMessage({ id: 'avatarStudio.publish.actions' })}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.version.n} data-testid={`avatar-version-${row.version.n}`}>
            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" translate="no">
                  {formatMessage(
                    { id: 'avatarStudio.publish.versionN' },
                    { n: row.version.n },
                  )}
                </Typography>
                {row.isLive && (
                  <Chip
                    size="small"
                    color="success"
                    label={formatMessage({ id: 'avatarStudio.publish.live' })}
                  />
                )}
                {!row.isLive && (
                  <Tooltip
                    title={formatMessage({
                      id: 'avatarStudio.publish.neverPublishedWhy',
                    })}
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      tabIndex={0}
                      label={formatMessage({
                        id: 'avatarStudio.publish.neverPublished',
                      })}
                    />
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
            <TableCell>
              <Typography variant="caption">{row.version.stagedBy ?? '—'}</Typography>
            </TableCell>
            <TableCell>
              {row.declinedBy.length > 0 ? (
                <Tooltip title={row.declinedBy.map((d) => d.note ?? '').join(' ')}>
                  <Chip
                    size="small"
                    color="warning"
                    tabIndex={0}
                    data-testid={`declined-v${row.version.n}`}
                    label={formatMessage(
                      { id: 'avatarStudio.publish.declinedBy' },
                      { who: row.declinedBy.map((d) => d.approver).join(', ') },
                    )}
                  />
                </Tooltip>
              ) : (
                <Typography variant="caption">
                  {row.approvedBy.length ? row.approvedBy.join(', ') : '—'}
                </Typography>
              )}
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {onInspect && (
                  <Button
                    size="small"
                    onClick={() => onInspect(row.version.n)}
                    data-testid={`changes-v${row.version.n}`}
                  >
                    {formatMessage({ id: 'avatarStudio.changes.open' })}
                  </Button>
                )}
                {!row.isLive && (
                  <AvatarBoCan
                    perform={AVATAR_BO_PERMISSIONS.versionApprove}
                    // tslint:disable-next-line: jsx-no-lambda
                    yes={() => (
                      <Tooltip
                        title={
                          canApprove(row)
                            ? ''
                            : formatMessage({
                                id:
                                  row.verdict === 'declined'
                                    ? 'avatarStudio.publish.cannotApproveDeclined'
                                    : 'avatarStudio.publish.cannotApprove',
                              })
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            disabled={busy || !canApprove(row)}
                            onClick={() => onApprove(row.version.n)}
                            data-testid={`approve-v${row.version.n}`}
                          >
                            {formatMessage({ id: 'avatarStudio.publish.approve' })}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  />
                )}
                {!row.isLive && (
                  <AvatarBoCan
                    perform={AVATAR_BO_PERMISSIONS.versionApprove}
                    // tslint:disable-next-line: jsx-no-lambda
                    yes={() => (
                      <Tooltip
                        title={
                          canDecline(row)
                            ? ''
                            : formatMessage({
                                id:
                                  row.verdict === 'declined'
                                    ? 'avatarStudio.publish.cannotDeclineTwice'
                                    : 'avatarStudio.publish.cannotDecline',
                              })
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            color="warning"
                            disabled={busy || !canDecline(row)}
                            onClick={() => onDecline(row.version.n)}
                            data-testid={`decline-v${row.version.n}`}
                          >
                            {formatMessage({ id: 'avatarStudio.publish.decline' })}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  />
                )}
                <AvatarBoCan
                  perform={AVATAR_BO_PERMISSIONS.versionPublish}
                  // tslint:disable-next-line: jsx-no-lambda
                  yes={() => (
                    <Tooltip
                      title={
                        row.blockedReason
                          ? formatMessage(
                              {
                                id: `avatarStudio.publish.blocked.${row.blockedReason}`,
                              },
                              {
                                who: row.declinedBy
                                  .map((d) => d.approver)
                                  .join(', '),
                              },
                            )
                          : ''
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant={row.isRollback ? 'outlined' : 'contained'}
                          color={row.isRollback ? 'warning' : 'primary'}
                          disabled={busy || !canPublish(row)}
                          onClick={() => onPublish(row.version.n, row.isRollback)}
                        >
                          {formatMessage({
                            id: row.isRollback
                              ? 'avatarStudio.publish.rollbackTo'
                              : 'avatarStudio.publish.publish',
                          })}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
